import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Col,
  Form,
  Radio,
  Row,
  Select,
  Space,
  notification,
  Modal,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import DecisionFlow from "./DecisionFlow";
import { DataContext } from './DataContext';
import {
  getAllTShirtSizes,
  getProcurementDetails,
  updateProcurementStageItem,
} from "apis";
import { getUserIdentity } from "helpers/auth";
import { DatabaseManager } from "helpers/db";
import moment from "moment";
import {
  PROCUREMENT_TYPES,
  PROCUREMENT_STATUS,
  ENTITY_TYPES, 
  PROCUREMENT_STAGES, 
  buildStageSK,
} from "types";
import {
  extractEmailsFromString,
  generateDefaultProcurementName,
  get_html,
  replace_vars,
} from "helpers";

const { Option } = Select;

// Enterprise styling for form sections
const formSectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "24px",
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: "12px",
  marginBottom: "20px"
};

const style = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
  fontWeight: 500,
};

const DATA_CENTERS = new Set(["SGP", "CTC", "AME", "NLAME"]);
const KTC_SITE = "KTC";

const getDefaultTshirtSize = (siteCode) => {
  if (siteCode === KTC_SITE) return "DL-380";
  if (DATA_CENTERS.has(siteCode)) return "ALLETRA";
  return "DL-320"; // ROBO or others
};

// Server capacity configurations
const SERVER_CONFIGS = {
  "DL-380": { capacityPerNode: 133, maxNodesPerCluster: 12, minNodes: 3, totalCapacity: 400 },
  "DL-320": { capacityPerNode: 41, maxNodesPerCluster: 12, minNodes: 3, totalCapacity: 124 },
  "ALLETRA": { capacityPerNode: 333, maxNodesPerCluster: 3, minNodes: 3, totalCapacity: 1000 } // 1PB/3 nodes
};

// Check if existing cluster can be expanded based on generation
const canExpandCluster = (uniquePoolNames) => {
  // If uniquePoolNames has items, it means there are Gen11 systems that can be expanded
  return uniquePoolNames && uniquePoolNames.length > 0;
};

// Fixed projection calculation to match AmortizationTable (current plus 4 growth cycles)
const calculateProjectedUsage = (usedSpace, extraSpace, rate1 = 0.10, rate2 = 0.10, years = 4) => {
  // Parse values safely
  const safeUsedSpace = Number.isFinite(parseFloat(usedSpace)) ? parseFloat(usedSpace) : 0;
  const safeExtraSpace = Number.isFinite(parseFloat(extraSpace)) ? parseFloat(extraSpace) : 0;
  
  // Calculate Additional ROBO Storage growth (4 years: 2026, 2027, 2028, 2029)
  let backupValue = safeExtraSpace;
  for (let i = 0; i < years; i++) {
    backupValue = backupValue * (1 + rate1);
  }
  
  // Calculate Used Space growth (4 years: 2026, 2027, 2028, 2029)
  let usageValue = safeUsedSpace;
  for (let i = 0; i < years; i++) {
    usageValue = usageValue * (1 + rate2);
  }
  
  // Total projected usage in 2029 (should match AmortizationTable)
  return backupValue + usageValue;
};

// Calculate nodes needed based on gap and cluster type
const calculateNodesNeeded = (gap, isNewCluster, tshirtSize = "DL-320") => {
  const config = SERVER_CONFIGS[tshirtSize] || SERVER_CONFIGS["DL-320"];
  const capacityPerNode = config.capacityPerNode;
  const minNodes = config.minNodes;
  
  if (isNewCluster) {
    // New cluster: minimum 3 nodes, must be in multiples of 3
    const nodesNeeded = Math.ceil(gap / capacityPerNode);
    const adjustedNodes = Math.max(minNodes, Math.ceil(nodesNeeded / minNodes) * minNodes);
    return adjustedNodes;
  } else {
    // Expansion: just calculate nodes needed (max 12 nodes total per cluster)
    return Math.ceil(gap / capacityPerNode);
  }
};

// Get existing cluster t-shirt size from modelInfo
const getExistingClusterTshirtSize = (modelInfo, siteCode) => {
  if (!modelInfo || !modelInfo.tshirtSizes || modelInfo.tshirtSizes.length === 0) {
    return getDefaultTshirtSize(siteCode);
  }
  // Use the first t-shirt size found in existing clusters
  return modelInfo.tshirtSizes[0];
};

// Determine best server type based on requirements (for new clusters only)
const getBestServerTypeForNewCluster = (siteCode, gap) => {
  // KTC always uses DL-380
  if (siteCode === KTC_SITE) return "DL-380";
  
  // Data centers use ALLETRA
  if (DATA_CENTERS.has(siteCode)) return "ALLETRA";
  
  // For ROBO sites, calculate nodes needed for DL-320
  const dl320NodesNeeded = Math.ceil(gap / SERVER_CONFIGS["DL-320"].capacityPerNode);
  
  // If more than 6 DL-320 nodes needed, consider DL-380
  if (dl320NodesNeeded > 6) {
    return "DL-380";
  }
  
  return "DL-320";
};

const MasterActionPage = ({
  siteCode, 
  siteName, 
  setSiteCode, 
  setShouldCallAPI, 
  Commserve, 
  uniquePoolNames, 
  setIsSubmitting,
  isUserAllowed,
  usedSpace,
  totalSpace,
  modelInfo,
  gen11storagepools
}) => {

  // console.log(modelInfo);
  // console.log(gen11storagepools);

  const [radioValue, setRadioValue] = useState(1);
  const [nodeCount, setNodeCount] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState("");
  const [selectedClusterTshirtSize, setSelectedClusterTshirtSize] = useState("");
  const [clusterSizesForExpProcurement, setClusterSizesForExpProcurement] = useState([]);
  const [commvaultStageData, setCommvaultStageData] = useState(null);
  const [shouldDisable, setShouldDisable] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Add SFP selection state
  const [sfpOption, setSfpOption] = useState("10gb");
  
  // State for modal visibility
  const [isDecisionFlowVisible, setIsDecisionFlowVisible] = useState(false);

  // Get storage and growth rates from DataContext
  const { sharedData } = useContext(DataContext);

  const DBManager = new DatabaseManager();

  // Safe parsing of values
  const safeStorage = Number.isFinite(parseFloat(sharedData?.initialValue)) ? parseFloat(sharedData?.initialValue) : 0;
  const safeUsedSpace = Number.isFinite(parseFloat(usedSpace)) ? parseFloat(usedSpace) : 0;
  const safeTotalSpace = Number.isFinite(parseFloat(totalSpace)) ? parseFloat(totalSpace) : 0;

  // Get user-selected rates from shared data
  const userRate1 = sharedData?.rate1 ? sharedData.rate1 / 100 : 0.10; // Convert percentage to decimal
  const userRate2 = sharedData?.rate2 ? sharedData.rate2 / 100 : 0.10; // Convert percentage to decimal

  // Calculate projected usage and capacity limit for passing to DecisionFlow
  const extraSpace = commvaultStageData?.extra_space || safeStorage;
  const projectedUsage = calculateProjectedUsage(safeUsedSpace, extraSpace, userRate1, userRate2);
  const capacityLimit = safeTotalSpace * 0.8;
  const canExpand = canExpandCluster(uniquePoolNames);

  // Create a unique PK for master procurement (using Commserve)
  const getPrimaryKey = () => `${siteCode}-${siteName}-${new Date().toISOString()}`;

  // Add function to generate CV value
  const generateCVValue = (nodeCount, tshirtSize, sfpOption) => {
    if (!nodeCount || !tshirtSize) return "";
    
    let capacity = "";
    if (tshirtSize === "DL-320") {
      capacity = "124tb";
    } else if (tshirtSize === "DL-380") {
      capacity = "400tb";
    } else if (tshirtSize === "ALLETRA") {
      capacity = "1000tb";
    }
    
    return `${nodeCount} Node ${tshirtSize}-${sfpOption}-sfp-${capacity} Gen11`;
  };

  // Fetch extra space and stage data on mount
  const fetchExtraSpaceAndStageData = async () => {
    try {
      // For master, we might not have existing data initially
      setRadioValue(1);
      setNodeCount(null);
      setSelectedCluster("");
      setSelectedClusterTshirtSize(getDefaultTshirtSize(siteCode));
      setSfpOption("10gb");
    } catch (error) {
      console.error(error);
    }
  };

  // New function to calculate and set defaults on button click
  const handleEstimate = () => {
    if (!siteCode) {
      notification.warning({
        message: "Please select a site before estimating Commvault resources.",
      });
      return;
    }

    if (!safeStorage || safeStorage === 0) {
      notification.warning({
        message: "Please enter Additional Backup Storage value in the amortization table before estimating.",
      });
      return;
    }

    const projectedUsage = calculateProjectedUsage(safeUsedSpace, safeStorage, userRate1, userRate2);
    const capacityLimit = safeTotalSpace * 0.8;

    if (projectedUsage <= capacityLimit) {
      setRadioValue(1);
      setNodeCount(null);
      setSelectedCluster("");
      setSelectedClusterTshirtSize(getDefaultTshirtSize(siteCode));
      notification.success({
        message: "Estimation Complete: No new resources required",
        description: `Projected usage (${projectedUsage.toFixed(2)} TB) is within 80% capacity limit (${capacityLimit.toFixed(2)} TB).`
      });
    } else {
      const gap = projectedUsage - capacityLimit;
      const canExpand = canExpandCluster(uniquePoolNames);
      
      if (canExpand) {
        // For expansion, use existing cluster's t-shirt size
        const existingTshirtSize = getExistingClusterTshirtSize(modelInfo, siteCode);
        const nodesNeededForExpansion = calculateNodesNeeded(gap, false, existingTshirtSize);
        
        // Check if expansion would exceed max nodes (12 nodes total, 3 existing + expansion)
        if (nodesNeededForExpansion <= 9) {
          setRadioValue(3);
          setNodeCount(nodesNeededForExpansion);
          setSelectedCluster(uniquePoolNames[0] || "");
          setSelectedClusterTshirtSize(existingTshirtSize);
          notification.success({
            message: "Estimation Complete: Expansion recommended",
            description: `Need ${nodesNeededForExpansion} additional nodes (${gap.toFixed(2)} TB gap). Expanding existing cluster: ${uniquePoolNames[0]} with ${existingTshirtSize}`
          });
        } else {
          // Expansion would exceed 12 nodes - create new cluster with optimized server type
          const bestServerType = getBestServerTypeForNewCluster(siteCode, gap);
          const newClusterNodes = calculateNodesNeeded(gap, true, bestServerType);
          setRadioValue(2);
          setNodeCount(newClusterNodes);
          setSelectedCluster("");
          setSelectedClusterTshirtSize(bestServerType);
          notification.success({
            message: "Estimation Complete: New cluster creation recommended",
            description: `Expansion would exceed 12 nodes. Creating new ${bestServerType} cluster with ${newClusterNodes} nodes (${gap.toFixed(2)} TB gap).`
          });
        }
      } else {
        // Cannot expand (no existing Gen11 pools) - create new cluster with optimized server type
        const bestServerType = getBestServerTypeForNewCluster(siteCode, gap);
        const newClusterNodes = calculateNodesNeeded(gap, true, bestServerType);
        setRadioValue(2);
        setNodeCount(newClusterNodes);
        setSelectedCluster("");
        setSelectedClusterTshirtSize(bestServerType);
        
        notification.success({
          message: "Estimation Complete: New cluster creation recommended",
          description: `Creating new ${bestServerType} cluster with ${newClusterNodes} nodes (${gap.toFixed(2)} TB gap).`
        });
      }
    }
  };

  // Function to show decision flow modal
  const showDecisionFlow = () => {
    if (!siteCode) {
      notification.warning({
        message: "Please select a site to view the decision flow.",
      });
      return;
    }

    if (!safeStorage || safeStorage === 0) {
      notification.warning({
        message: "Please enter Additional Backup Storage value in the amortization table before viewing decision flow.",
      });
      return;
    }

    setIsDecisionFlowVisible(true);
  };

  // Function to hide decision flow modal
  const hideDecisionFlow = () => {
    setIsDecisionFlowVisible(false);
  };

  const handleButtonEntrypoint = async () => {
    console.log("inside handleButtonEntrypoint")
  }

  const handleUpdateButton = async () => {
    setIsSubmitting(true)
    console.log("inside handleUpdateButton")
    
    const primaryKey = getPrimaryKey();
    const user_email = (await getUserIdentity()).email;
    const created_at_timestamp = moment().toISOString();

    let commvaultprocurementType = PROCUREMENT_TYPES.NONE;
    switch (radioValue) {
      case 1:
        commvaultprocurementType = PROCUREMENT_TYPES.NONE;
        break;
      case 2:
        commvaultprocurementType = PROCUREMENT_TYPES.NEW;
        break;
      case 3:
        commvaultprocurementType = PROCUREMENT_TYPES.EXPANSION;
        break;
      default:
        commvaultprocurementType = PROCUREMENT_TYPES.NONE; // fallback
    }

    await new Promise((resolve) => setTimeout(resolve, 2500));

    // Find the matching storage pool's tshirt size from gen11storagepools
    const matchingPool = gen11storagepools?.find(pool => pool.storagePoolName === selectedCluster);
    const expansionTshirtSize = matchingPool?.tshirtSize || modelInfo?.tshirtSizes?.[0];

    // Generate CV value
    const cvValue = generateCVValue(
      nodeCount, 
      radioValue === 3 ? expansionTshirtSize : selectedClusterTshirtSize, 
      sfpOption
    );

    try {
      await DBManager.createObject(
        process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
        {
          PK: primaryKey,
          SK: primaryKey,
          procurement_name: primaryKey,
          entity_type: "Procurement",
          procurement_status: PROCUREMENT_STATUS.Initial,
          site_code: siteCode,
          site_name: siteName,
          created_at: created_at_timestamp,
          created_by: user_email,
          procurement_type: commvaultprocurementType,
          region: Commserve.split('.')[0].slice(-4)
        }
      );

      await DBManager.createObject(
        process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
        {
          PK: primaryKey,
          SK: buildStageSK(PROCUREMENT_STAGES.CommVault),
          entity_type: ENTITY_TYPES.ProcurementStage,
          stage_name: PROCUREMENT_STAGES.CommVault,
          created_at: created_at_timestamp,
          created_by: user_email,
          stage_status: PROCUREMENT_STATUS.Initial,
          commvault_procurement_type: commvaultprocurementType,
          storage_pool: selectedCluster,
          cluster_tshirt_size: radioValue === 3 ? expansionTshirtSize : selectedClusterTshirtSize,
          node: nodeCount,
          site_code: siteCode,
          site_name: siteName,
          extra_space: sharedData?.initialValue || 0,
          sfp_option: sfpOption,
          cv: cvValue,
          // Save growth rate configurations - only save custom rate when type is "custom"
          additional_storage_increase_type: sharedData?.increaseType1 || "10",
          additional_storage_custom_rate: sharedData?.increaseType1 === "custom" ? (sharedData?.customRate1 || 0) : 0,
          used_space_increase_type: sharedData?.increaseType2 || "10",
          used_space_custom_rate: sharedData?.increaseType2 === "custom" ? (sharedData?.customRate2 || 0) : 0,
        }
      );

      //new
      const response = await getProcurementDetails(primaryKey, "CommVault");
      if (response.success && response.data?.length) {
        const stageItem = response.data[0];
        console.log(stageItem)
        const updatedData = {
          ...stageItem,
          stage_status: PROCUREMENT_STATUS.Submitted,
        };
        console.log(updatedData);
        const updateResponse = await updateProcurementStageItem(updatedData);
      }

      setIsSubmitted(true);
      notification.success({
        message: "Master Commvault Order Complete !",
      });
    } catch (error) {
      console.log(error)
      notification.error({
        message: "Master Commvault Assessment Failed !",
      });
    }
    finally {
      setIsSubmitting(false)
    }
  };
  
  const handleApproveButton = async () => {
    console.log("inside handleApproveButton")
  };

  const handleRadioChange = (e) => {
    setRadioValue(e.target.value);
  };

  const getTshirtSizesData = async () => {
    setClusterSizesForExpProcurement(["DL-380", "DL-320", "ALLETRA"])
  };

  useEffect(() => {
    getTshirtSizesData();
    handleButtonEntrypoint();
    fetchExtraSpaceAndStageData();
    console.log(`inside useeffect ${siteCode}`)
  }, []);
  
  const nodeOptions = Array.from({ length: 9 }, (_, i) => (
    <Option key={i + 1} value={i + 1}>
      {i + 1}
    </Option>
  ));
  
  const clusterOptions = uniquePoolNames ? uniquePoolNames.map((cluster) => (
    <Option key={cluster} value={cluster}>
      {cluster}
    </Option>
  )) : [];

  // Add SFP options
  const sfpOptions = [
    { label: "10 GB SFP", value: "10gb" },
    { label: "25 GB SFP", value: "25gb" }
  ];

  return (
    <>
      <Form disabled={!isUserAllowed}>
        <Row>
          <Col span={24}>
            <Radio.Group
              style={style}
              onChange={handleRadioChange}
              value={radioValue}
              disabled={shouldDisable || !isUserAllowed}
            >
              <Radio value={1}>No new CommVault resources required</Radio>
              <Radio value={2}>New Commvault cluster</Radio>
              {uniquePoolNames && uniquePoolNames.length > 0 && (
                <Radio value={3}>Expand Commvault cluster</Radio>
              )}     
            </Radio.Group>
          </Col>

          {/* Action Buttons Row */}
          <Row style={{ marginTop: 20, marginRight: 50 }}>
            <Col span={24}>
              <Space>
                <Button
                  type="primary"
                  onClick={handleEstimate}
                  disabled={!isUserAllowed}
                >
                  Estimate Commvault
                </Button>
                <Button
                  type="default"
                  icon={<EyeOutlined />}
                  onClick={showDecisionFlow}
                  disabled={!isUserAllowed}
                >
                  View Decision Flow
                </Button>
              </Space>
            </Col>
          </Row>

          {radioValue === 2 && (
            <>
              <Col span={6} style={{ marginTop: 16, marginRight: 50 }}>
                <Form.Item label="Cluster Tshirt Size">
                  <Select
                    placeholder="Select Cluster Tshirt Size"
                    value={selectedClusterTshirtSize}
                    onChange={setSelectedClusterTshirtSize}
                    disabled={shouldDisable || !isUserAllowed}
                    options={clusterSizesForExpProcurement.map((item) => {
                      return {
                        label: item,
                        value: item,
                      };
                    })}
                  />
                </Form.Item>
              </Col>
              <Col span={4} style={{ marginTop: 16, marginRight: 50 }}>
                <Form.Item label="Number of Nodes">
                  <Select
                    placeholder="Select number of nodes"
                    value={nodeCount}
                    onChange={setNodeCount}
                    disabled={shouldDisable || !isUserAllowed}
                  >
                    {nodeOptions}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6} style={{ marginTop: 16 }}>
                <Form.Item label="SFP Option">
                  <Select
                    placeholder="Select SFP option"
                    value={sfpOption}
                    onChange={setSfpOption}
                    disabled={shouldDisable || !isUserAllowed}
                    options={sfpOptions}
                  />
                </Form.Item>
              </Col>
            </>
          )}
          
          {radioValue === 3 && uniquePoolNames && uniquePoolNames.length > 0 && (
            <>
              <Col span={6} style={{ marginTop: 16, marginRight: 30 }}>
                <Form.Item label="Select Existing Cluster">
                  <Select
                    placeholder="Select cluster (storage pool)"
                    value={selectedCluster}
                    onChange={setSelectedCluster}
                    disabled={shouldDisable || !isUserAllowed}
                  >
                    {clusterOptions}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6} style={{ marginTop: 16, marginRight: 30 }}>
                <Form.Item label="Number of Additional Nodes">
                  <Select
                    placeholder="Select number of nodes"
                    value={nodeCount}
                    onChange={setNodeCount}
                    disabled={shouldDisable || !isUserAllowed}
                  >
                    {nodeOptions}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6} style={{ marginTop: 16 }}>
                <Form.Item label="SFP Option">
                  <Select
                    placeholder="Select SFP option"
                    value={sfpOption}
                    onChange={setSfpOption}
                    disabled={shouldDisable || !isUserAllowed}
                    options={sfpOptions}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        
        <Row>
          <Col span={24}>
            <Space style={{ marginTop: "35px" }}>
              <Form.Item>
                <Button 
                  type="primary" 
                  onClick={handleUpdateButton}  
                  disabled={shouldDisable || !siteCode || !isUserAllowed}
                >
                  Order
                </Button>
              </Form.Item>
            </Space>
          </Col>
        </Row>
      </Form>

      {/* Decision Flow Modal */}
      <Modal
        title="Master CommVault Decision Flow Analysis"
        open={isDecisionFlowVisible}
        onCancel={hideDecisionFlow}
        footer={null}
        width="75vw"
        style={{ top: '10vh', maxWidth: '1500px' }}
        bodyStyle={{ padding: 10, height: '80vh', overflow: 'hidden' }}
        destroyOnClose={true}
        centered={false}
      >
        <DecisionFlow
          siteCode={siteCode}
          projectedUsage={projectedUsage}
          capacityLimit={capacityLimit}
          canExpand={canExpand}
          radioValue={radioValue}
          uniquePoolNames={uniquePoolNames || []}
          usedSpace={safeUsedSpace}
          totalSpace={safeTotalSpace}
          storage={extraSpace}
          modelInfo={modelInfo}
        />
      </Modal>
    </>
  );
};

export default MasterActionPage;